import { customFetch } from '@agos/api-client'
import { useQuery } from '@tanstack/react-query'
import type { ProductCosts } from '@agos/api-client/model'

/**
 * Per-product cost and margin.
 *
 * `/products/costs` is untyped by the backend, so this shape was read off the
 * running instance against the restored dump — see
 * `docs/pages/manager-menu/products.md`.
 *
 * FB-032: legacy wraps the call in `.catch(() => {})`. When it fails every
 * product shows no cost and no margin, which is indistinguishable from a menu
 * nobody has costed — a franchise could conclude its margins are unknown when
 * the request simply 500'd. The caller here can tell the two apart.
 */

export interface SizeCost {
  label: string
  cm: number
  cost: number | null
  price: number
  marginPercent: number | null
  ingredientsWithCost: number
  ingredientCount: number
}

export interface ProductCost {
  /** Null means "not known", which is not the same as zero. */
  estimatedCost: number | null
  marginPercent: number | null
  /**
   * How many of the product's ingredients actually carry a cost. A margin from
   * three costed ingredients out of eleven is not a margin, and the screen
   * shows the ratio so nobody reads it as one.
   */
  ingredientsWithCost: number
  ingredientCount: number
  sizeCosts: SizeCost[] | null
}

interface RawSizeCost {
  label?: unknown
  cm?: unknown
  cost?: unknown
  price?: unknown
  margin_pct?: unknown
  ingredients_with_cost?: unknown
  ingredient_count?: unknown
}

/* `RawProductCost` was here - §9.416 removed it. It declared the same six `snake_case` fields the
 * server declares on `ProductCost`, so `Partial<ProductCosts>` at the call now gives `raw` the
 * generated element type and a rename becomes a compile error. The local `ProductCost` below is this
 * hook's **own** camelCase output and is untouched; only the plural is imported, so there is no
 * collision. The `optionalNumber`/`countOf` coercions stay: the type is the promise, the coercion is
 * for a value that arrives as a string from a numeric column. */

/** Null and empty stay null; only a real number becomes one. */
function optionalNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function countOf(value: unknown): number {
  return Number(value) || 0
}

/** The endpoint is untyped, so a label could be anything. */
function textOf(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

export function useProductCosts() {
  return useQuery({
    queryKey: ['products', 'costs'],
    queryFn: async (): Promise<Map<number, ProductCost>> => {
      const response = await customFetch<Partial<ProductCosts>>('/api/v1/products/costs')
      const byProductId = new Map<number, ProductCost>()

      for (const raw of response?.costs ?? []) {
        const productId = optionalNumber(raw.id)
        if (productId === null) continue

        byProductId.set(productId, {
          estimatedCost: optionalNumber(raw.estimated_cost),
          marginPercent: optionalNumber(raw.margin_pct),
          ingredientsWithCost: countOf(raw.ingredients_with_cost),
          ingredientCount: countOf(raw.ingredient_count),
          sizeCosts: Array.isArray(raw.size_costs)
            ? (raw.size_costs as RawSizeCost[]).map((size) => ({
                label: textOf(size.label),
                cm: countOf(size.cm),
                cost: optionalNumber(size.cost),
                price: countOf(size.price),
                marginPercent: optionalNumber(size.margin_pct),
                ingredientsWithCost: countOf(size.ingredients_with_cost),
                ingredientCount: countOf(size.ingredient_count),
              }))
            : null,
        })
      }

      return byProductId
    },
  })
}
